/**
 * SidebetButtonsOverlay
 *
 * DOM overlay showing 4 sidebet hex icon buttons in a 2x2 grid.
 * Positioned in the space between the sidebet create zone's right edge
 * and the screen's right edge, vertically centered on the zone.
 */

import * as THREE from 'three';
import gsap from 'gsap';
import { worldToScreen, getCanvasSize } from '../../engine/utils/worldToScreen';

/** Sidebet option definition */
export interface SidebetOption {
  /** Label sent to server (e.g. 'BANK WINS') */
  label: string;
  /** Path to hex icon image */
  icon: string;
}

import { sidebetCircleIcon } from './sidebetIcon';

/**
 * Full registry of sidebet options across all game types. The overlay shows
 * a subset selected by `setAvailableOptions(labels)`.
 *
 * Every option uses the same round-circle SVG style (see sidebetIcon.ts).
 * The `label` is the server-facing enum string sent through betTypeMap in
 * main.ts (e.g. 'BW' → 'shooter_wins').
 */
export const SIDEBET_OPTIONS: SidebetOption[] = [
  // Cee-lo
  { label: 'BANK WINS',  icon: sidebetCircleIcon('BW', '#00FF88') },
  { label: 'BANK LOSES', icon: sidebetCircleIcon('BL', '#FF3366') },
  { label: '4+',         icon: sidebetCircleIcon('4+', '#FFD700') },
  { label: '3-',         icon: sidebetCircleIcon('3-', '#00F5FF') },
  // Craps — pre-come-out (turn-level). 'SW'/'SL' so the chips are
  // unambiguous next to cee-lo's 'BW'/'BL' across the codebase.
  { label: 'SW',         icon: sidebetCircleIcon('SW', '#00FF88') },
  { label: 'SL',         icon: sidebetCircleIcon('SL', '#FF3366') },
  // Craps — mid-point (multi-roll point pair)
  { label: '4/10',       icon: sidebetCircleIcon('4/10', '#FFD700') },
  { label: '5/9',        icon: sidebetCircleIcon('5/9',  '#00F5FF') },
  { label: '6/8',        icon: sidebetCircleIcon('6/8',  '#FF0080') },
];

export interface SidebetButtonsOverlayConfig {
  container: HTMLElement;
  camera: THREE.PerspectiveCamera;
  canvas: HTMLCanvasElement;
}

/** World-unit radius of the sidebet create zone circle */
const ZONE_RADIUS = 1.1;

export class SidebetButtonsOverlay {
  private container: HTMLElement;
  private camera: THREE.PerspectiveCamera;
  private canvas: HTMLCanvasElement;
  private root: HTMLElement;
  private grid: HTMLElement;
  private buttons: HTMLElement[] = [];
  private isVisible = false;
  private onSelect: ((label: string) => void) | null = null;

  /** World position of the create zone center */
  private anchorWorldPos: THREE.Vector3 | null = null;

  /**
   * Cached grid dimensions. Measuring offsetWidth/offsetHeight forces a layout;
   * the grid only changes size when the visible option set changes
   * (setAvailableOptions) or a media-query breakpoint flips on resize, so we
   * measure lazily and invalidate on those events instead of every frame.
   * 0 = needs (re)measure.
   */
  private gridW = 0;
  private gridH = 0;
  /** Last written position — skip redundant style writes when the camera is still. */
  private lastLeft = NaN;
  private lastTop = NaN;
  private readonly onResize = () => { this.gridW = 0; this.gridH = 0; };

  constructor(config: SidebetButtonsOverlayConfig) {
    this.container = config.container;
    this.camera = config.camera;
    this.canvas = config.canvas;
    window.addEventListener('resize', this.onResize);

    this.root = document.createElement('div');
    this.root.className = 'sidebet-buttons-overlay';
    this.container.appendChild(this.root);

    this.grid = document.createElement('div');
    this.grid.className = 'sidebet-buttons-grid';
    this.root.appendChild(this.grid);

    this.createButtons();
    this.injectStyles();
  }

  private createButtons(): void {
    for (const option of SIDEBET_OPTIONS) {
      const btn = document.createElement('button');
      btn.className = 'sidebet-btn';
      btn.dataset.label = option.label;

      const img = document.createElement('img');
      img.src = option.icon;
      img.alt = option.label;
      img.draggable = false;
      btn.appendChild(img);

      btn.addEventListener('pointerdown', (e) => {
        e.stopPropagation();
        e.preventDefault();
        this.handleSelect(option.label);
      });

      this.grid.appendChild(btn);
      this.buttons.push(btn);
    }
  }

  private handleSelect(label: string): void {
    if (!this.isVisible) return;
    this.onSelect?.(label);
  }

  /** Set callback for when a sidebet is selected */
  setOnSelect(cb: (label: string) => void): void {
    this.onSelect = cb;
  }

  /** Set the world position of the create zone center */
  setAnchorPosition(worldPos: { x: number; y: number; z: number }): void {
    if (!this.anchorWorldPos) {
      this.anchorWorldPos = new THREE.Vector3(worldPos.x, worldPos.y, worldPos.z);
    } else {
      this.anchorWorldPos.set(worldPos.x, worldPos.y, worldPos.z);
    }
  }

  /**
   * Toggle which sidebet options are offered. Pass the labels exactly as in
   * SIDEBET_OPTIONS (e.g. '4+', 'BANK WINS'). Buttons not in the list are
   * hidden from the grid (display: none) so layout collapses to N buttons.
   */
  setAvailableOptions(labels: string[]): void {
    const allowed = new Set(labels);
    for (const btn of this.buttons) {
      const label = btn.dataset.label ?? '';
      btn.style.display = allowed.has(label) ? '' : 'none';
    }
    // Visible-button set changed → grid size may have changed; re-measure.
    this.gridW = 0;
    this.gridH = 0;
  }

  /** Show the buttons with a pop-in animation */
  show(): void {
    if (this.isVisible) return;
    this.isVisible = true;
    this.root.classList.add('visible');
    this.updateScreenPosition();

    // Animate only visible buttons (those not hidden by setAvailableOptions)
    const visibleButtons = this.buttons.filter((b) => b.style.display !== 'none');
    visibleButtons.forEach((btn, i) => {
      gsap.fromTo(btn,
        { scale: 0, opacity: 0 },
        {
          scale: 1,
          opacity: 1,
          duration: 0.25,
          ease: 'back.out(1.7)',
          delay: i * 0.06,
        }
      );
    });
  }

  /** Hide the buttons */
  hide(): void {
    if (!this.isVisible) return;
    this.isVisible = false;

    // Quick scale-out
    this.buttons.forEach((btn, i) => {
      gsap.to(btn, {
        scale: 0,
        opacity: 0,
        duration: 0.15,
        ease: 'power2.in',
        delay: i * 0.03,
        onComplete: () => {
          if (i === this.buttons.length - 1) {
            this.root.classList.remove('visible');
          }
        },
      });
    });
  }

  /**
   * Update screen position each frame.
   *
   * Strategy:
   * 1. Project zone center and its right edge (center.x + radius) to screen
   * 2. The available strip is from the zone's right screen edge → canvas right edge
   * 3. Center the 2×2 grid horizontally in that strip, vertically on the zone center
   */
  updateScreenPosition(): void {
    if (!this.isVisible || !this.anchorWorldPos) return;

    // Project zone center
    const centerScreen = worldToScreen(this.anchorWorldPos, this.camera, this.canvas);
    if (!centerScreen) return;

    // Project a point at the zone's right edge (world X + radius, same Y/Z)
    const rightEdgeWorld = {
      x: this.anchorWorldPos.x + ZONE_RADIUS,
      y: this.anchorWorldPos.y,
      z: this.anchorWorldPos.z,
    };
    const rightEdgeScreen = worldToScreen(rightEdgeWorld, this.camera, this.canvas);
    if (!rightEdgeScreen) return;

    const screenRight = getCanvasSize(this.canvas).width;
    const zoneRightX = rightEdgeScreen.x;

    // Available space to the right of the zone
    const availableWidth = screenRight - zoneRightX;

    // Measure the grid lazily; cached until the option set / viewport changes.
    if (this.gridW === 0) this.gridW = this.grid.offsetWidth || 126; // fallback: 2×56 + gap + padding
    if (this.gridH === 0) this.gridH = this.grid.offsetHeight || 126;

    // Center the grid horizontally in the available space, vertically on the zone
    const gridLeft = zoneRightX + (availableWidth - this.gridW) / 2;
    const gridTop = centerScreen.y - this.gridH / 2;

    // Skip the style write (which dirties layout) when nothing moved.
    if (gridLeft !== this.lastLeft || gridTop !== this.lastTop) {
      this.root.style.left = `${gridLeft}px`;
      this.root.style.top = `${gridTop}px`;
      this.lastLeft = gridLeft;
      this.lastTop = gridTop;
    }
  }

  /** Call per frame to keep position synced with camera */
  update(): void {
    if (this.isVisible) {
      this.updateScreenPosition();
    }
  }

  getVisible(): boolean {
    return this.isVisible;
  }

  private injectStyles(): void {
    if (document.getElementById('sidebet-buttons-styles')) return;

    const styles = document.createElement('style');
    styles.id = 'sidebet-buttons-styles';
    styles.textContent = `
      .sidebet-buttons-overlay {
        position: absolute;
        pointer-events: none;
        z-index: 44;
        display: none;
      }

      .sidebet-buttons-overlay.visible {
        display: block;
      }

      .sidebet-buttons-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 6px;
        padding: 4px;
      }

      .sidebet-btn {
        pointer-events: auto;
        background: none;
        border: none;
        padding: 0;
        margin: 0;
        cursor: pointer;
        width: 56px;
        height: 56px;
        display: grid;
        place-items: center;
        -webkit-tap-highlight-color: transparent;
        touch-action: manipulation;
        transform-origin: center;
        will-change: transform;
        transition: filter 0.15s ease;
      }

      .sidebet-btn img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        pointer-events: none;
        filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.5));
      }

      .sidebet-btn:active {
        filter: brightness(1.3);
      }

      /* Scale buttons based on viewport for responsiveness */
      @media (max-width: 380px) {
        .sidebet-btn {
          width: 46px;
          height: 46px;
        }
        .sidebet-buttons-grid {
          gap: 4px;
        }
      }

      @media (min-width: 768px) {
        .sidebet-btn {
          width: 64px;
          height: 64px;
        }
        .sidebet-buttons-grid {
          gap: 8px;
        }
      }
    `;
    document.head.appendChild(styles);
  }

  destroy(): void {
    window.removeEventListener('resize', this.onResize);
    this.root.remove();
    const styles = document.getElementById('sidebet-buttons-styles');
    styles?.remove();
  }
}
